import React, { useMemo } from 'react';
import { Box, Stepper } from '@lucid/lego';
import type { Step } from '@lucid/lego';
import { getMappedOrderStatus } from '../../../orderStatusMapping';
import styles from './OrderStatusStepper.module.scss';

type OrderStatusStepperProps = {
  orderIdentifier?: string;
  orderStatus?: string;
  theme: 'light' | 'dark';
  labels: Record<string, string>;
  dataTestId?: string;
};

/** Map of canonical order status → step index. */
const STATUS_STEP_MAP: Record<string, number> = {
  'awaiting assignment': 0,
  'planning production': 1,
  'production planned': 2,
  'in production': 3,
  'factory gated': 4,
  'in transit to eu fpr': 5,
  'at eu fpr': 6,
  dispatched: 7,
  'on-site': 8,
  delivered: 9,
  cancelled: 9,
};

/**
 * Builds the ordered step definitions with localised labels.
 */
const buildStepDefinitions = (labels: Record<string, string>, isCancelled: boolean): Step[] => [
  {
    id: 'awaiting-assignment',
    label: labels?.awaitingAssignment || 'Awaiting assignment',
    status: 'pending',
  },
  {
    id: 'planning-production',
    label: labels?.planningProduction || 'Planning Production',
    status: 'pending',
  },
  {
    id: 'production-planned',
    label: labels?.productionPlanned || 'Production Planned',
    status: 'pending',
  },
  { id: 'in-production', label: labels?.inProduction || 'In Production', status: 'pending' },
  { id: 'factory-gated', label: labels?.factoryGated || 'Factory Gated', status: 'pending' },
  {
    id: 'in-transit-to-eu-fpr',
    label: labels?.inTransitToEuFpr || 'In Transit to EU FPR',
    status: 'pending',
  },
  {
    id: 'at-eu-fpr',
    label: labels?.atEuFpr || 'At EU FPR',
    status: 'pending',
  },
  {
    id: 'dispatched',
    label: labels?.dispatched || 'Dispatched',
    status: 'pending',
  },
  { id: 'on-site', label: labels?.onSite || 'On-site', status: 'pending' },
  {
    id: isCancelled ? 'cancelled' : 'delivered',
    label: isCancelled ? labels?.cancelled || 'Cancelled' : labels?.delivered || 'Delivered',
    status: 'pending',
  },
];

/**
 * Order status stepper — renders a horizontal progress bar reflecting the
 * current fulfilment stage of an order.
 *
 * @component
 * @param {OrderStatusStepperProps} props
 * @returns {JSX.Element}
 * @example
 * <OrderStatusStepper orderStatus="In Production" theme="dark" labels={labels} />
 */
const OrderStatusStepper: React.FC<OrderStatusStepperProps> = ({
  orderStatus,
  theme,
  labels,
  dataTestId = 'order-status-stepper',
}) => {
  const invertedTheme = theme === 'dark' ? 'light' : 'dark';

  const steps = useMemo<Step[]>(() => {
    const mappedStatus = getMappedOrderStatus(orderStatus);
    const isCancelled = mappedStatus === 'cancelled';
    const definitions = buildStepDefinitions(labels, isCancelled);

    if (isCancelled) {
      definitions[definitions.length - 1].status = 'completed';
    } else {
      const currentIndex = STATUS_STEP_MAP[mappedStatus] ?? 0;

      definitions.forEach((step, index) => {
        if (index < currentIndex) {
          step.status = 'completed';
        } else if (index === currentIndex) {
          step.status = mappedStatus === 'delivered' ? 'completed' : 'active';
        }
      });
    }

    return definitions;
  }, [orderStatus, labels]);

  return (
    <Box
      mt={16}
      pt={32}
      pl={24}
      pb={32}
      pr={24}
      mb={24}
      background={theme === 'dark' ? 'white' : 'black'}
      className={styles.stepperContainer}
    >
      <Stepper steps={steps} theme={invertedTheme} dataTestId={dataTestId} />
    </Box>
  );
};

OrderStatusStepper.displayName = 'OrderStatusStepper';

export default OrderStatusStepper;
